import { Auth0ContextInterface, useAuth0 } from '@auth0/auth0-react'
import { setContext, setTag, setUser } from '@sentry/gatsby'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { catchError, from, map, of, switchMap, throwError } from 'rxjs'

import useSiteMetadata from '../hooks/useSiteMetadata'
import { TokenContext } from '../layouts/context'
import { latestAccessTokenExpiration$ } from '../lib/auth/expiration'
import { markPageAuthenticated } from '../lib/auth/page-auth-age'
import { decodeTokenCustomerId } from '../lib/auth/scope'
import {
  latestAccessToken$,
  latestAccessTokenFactory$,
  latestAuthState$,
  latestAuthStateWithAccessToken$,
  latestLabelName$,
  latestLocalLogout$,
  setAccessToken,
  setAccessTokenFactory,
  setAuthState,
  setLocalLogout,
} from '../lib/auth/state'
import { dispensaryEffects$ } from '../lib/dispensaries/effects'
import { userClaimIds } from '../utils/userClaims'
import { resetTokenStore$ } from './AuthorizedApolloProvider'

// Session-lifetime auth telemetry, tracked at module scope so error events can show a transition the
// final auth snapshot hides. Sentry issue DFH-CORP-SITES-JSG often shows an unauthenticated/guest user
// on the error event even though checkout began authenticated: the Auth0 session expired or degraded in
// place. `everAuthenticated` + `becameUnauthenticated` make that progression observable without leaking
// tokens or Auth0 metadata.
let everAuthenticated = false

interface AuthErrorContext {
  name: string
  code?: string
}

interface AccessTokenState {
  token: string | undefined
  lastError: AuthErrorContext | undefined
}

const safeErrorCodes = new Set([
  'access_denied',
  'account_selection_required',
  'cancelled',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'login_required',
  'mfa_required',
  'missing_refresh_token',
  'timeout',
])

const safeErrorNames = new Set([
  'DOMException',
  'Error',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'TypeError',
])

const allowlistedTelemetryValue = (
  value: unknown,
  allowedValues: ReadonlySet<string>,
): string | undefined => (typeof value === 'string' && allowedValues.has(value) ? value : undefined)

export const authErrorContext = (error: unknown): AuthErrorContext => {
  const name =
    (error instanceof Error ? allowlistedTelemetryValue(error.name, safeErrorNames) : undefined) ??
    (error instanceof Error ? 'Error' : 'UnknownError')
  const auth0Code =
    typeof error === 'object' && error !== null
      ? allowlistedTelemetryValue(Reflect.get(error, 'error'), safeErrorCodes)
      : null
  const genericCode =
    typeof error === 'object' && error !== null
      ? allowlistedTelemetryValue(Reflect.get(error, 'code'), safeErrorCodes)
      : null
  const code = auth0Code ?? genericCode ?? undefined

  return code ? { name, code } : { name }
}

const isConsentRequired = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && Reflect.get(error, 'error') === 'consent_required'

const authStateContext = (
  { isAuthenticated, isLoading, error: authError, user }: Auth0ContextInterface,
  token: string | undefined,
  accessTokenError: AuthErrorContext | undefined,
): { [key: string]: unknown } => ({
  isAuthenticated,
  isLoading,
  everAuthenticated,
  // A degraded/expired session: we saw an authenticated state earlier this page load but no longer.
  becameUnauthenticated: everAuthenticated && !isAuthenticated,
  // The page-auth age is stamped in sentry.config.ts `beforeSend` rather than here: this effect only
  // re-runs when auth state changes, so an age computed at this point freezes on a quiet tab.
  //
  // Presence booleans only. Keep the Auth0 user/ID-token claim distinct from the access-token claim:
  // either can be absent independently, and `useCustomerId()` reads only the latter.
  hasAuth0Id: Boolean(user?.sub),
  hasAuth0NetSuiteId: Boolean(user?.[userClaimIds.netSuiteId]),
  hasAccessToken: Boolean(token),
  hasAccessTokenNsCustomerId: Boolean(token && decodeTokenCustomerId(token)),
  lastAccessTokenError: accessTokenError,
  // Auth0 messages and stacks can contain URLs, identifiers, or token fragments. The production
  // Auth0 bundle inherits the generic `Error` name, so its allowlisted protocol code carries the
  // useful detail without relying on minified constructor names.
  error: authError ? authErrorContext(authError) : undefined,
})

/**
 * The searchable half of the auth telemetry. Contexts render on a single event; only tags are
 * indexed, so these are what let us ask what share of a checkout render-loop issue arrived with a
 * degraded session (sc-142309). Every value is a bounded enum so tag cardinality stays flat, and
 * none of them carry an identifier.
 */
type AuthStateTag =
  | 'loading'
  | 'guest'
  | 'degraded'
  | 'authenticated'
  | 'authenticated_unprovisioned'

const authStateTag = (
  { isAuthenticated, isLoading }: Auth0ContextInterface,
  token: string | undefined,
): AuthStateTag => {
  if (isLoading) {
    return 'loading'
  }
  if (!isAuthenticated) {
    // Authenticated earlier this page load but no longer: an expired or degraded session.
    return everAuthenticated ? 'degraded' : 'guest'
  }
  // Both an unresolved token and a token without the claim leave `useCustomerId()` undefined, which
  // is the state that decides whether checkout treats the shopper as a customer.
  return token && decodeTokenCustomerId(token) ? 'authenticated' : 'authenticated_unprovisioned'
}

export interface TokenContextProviderProps {
  children: React.ReactNode
}

const TokenContextProvider: React.FC<TokenContextProviderProps> = ({ children }) => {
  const auth0Context = useAuth0()
  const {
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    isAuthenticated,
    isLoading,
    logout,
    user,
  } = auth0Context
  const [accessTokenState, setAccessTokenState] = useState<AccessTokenState>({
    token: undefined,
    lastError: undefined,
  })
  const { token, lastError: accessTokenError } = accessTokenState

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        if (isAuthenticated) {
          const accessToken = await getAccessTokenSilently()

          if (active) {
            setAccessTokenState({ token: accessToken || undefined, lastError: undefined })
          }
        } else if (active) {
          // Retain the last renewal error so the next Sentry event can explain why the authenticated
          // session degraded. A later successful token request clears it.
          setAccessTokenState((current) =>
            current.token === undefined ? current : { ...current, token: undefined },
          )
        }
      } catch (error: unknown) {
        if (active) {
          setAccessTokenState({
            token: undefined,
            lastError: authErrorContext(error),
          })
        }
      }
    })()
    return () => {
      active = false
    }
  }, [isAuthenticated, getAccessTokenSilently])

  useEffect(() => {
    // update Sentry user for error reporting
    setUser(
      user
        ? {
            id: user[userClaimIds.netSuiteId],
            auth0_id: user.sub,
          }
        : null,
    )
  }, [user])

  // Record that we reached an authenticated state (and when), so a later degrade to unauthenticated is
  // detectable in the Auth State context below. Runs before that effect so the flags are current.
  useEffect(() => {
    if (isAuthenticated) {
      everAuthenticated = true
      markPageAuthenticated()
    }
  }, [isAuthenticated])

  // update Sentry context for error reporting
  useEffect(() => {
    setContext('Auth State', authStateContext(auth0Context, token, accessTokenError))
    setTag('auth.state', authStateTag(auth0Context, token))
    // Cleared on the next successful renewal, matching `lastAccessTokenError`. An undefined tag
    // value is dropped from the event rather than sent as a stale one.
    setTag('auth.token_error', accessTokenError?.code)
  }, [auth0Context, token, accessTokenError])

  useEffect(() => {
    const subscriptions = [
      latestAuthState$.subscribe(),
      latestAccessToken$.subscribe(),
      latestAccessTokenFactory$.subscribe(),
      latestLocalLogout$.subscribe(),
      latestAuthStateWithAccessToken$.subscribe(),
      latestAccessTokenExpiration$.subscribe(),
      // hold subscription here to enable automatic dispensary redirects and notifications
      dispensaryEffects$.subscribe(),
    ]
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [])

  const { featureFlags } = useSiteMetadata() ?? {}
  useEffect((): void | (() => void) => {
    if (featureFlags?.productLabelStatus) {
      const subscription = latestLabelName$.subscribe()
      return () => subscription.unsubscribe()
    }
  }, [featureFlags])

  useEffect(() => {
    setAuthState({ isAuthenticated, isLoading, user })
  }, [isAuthenticated, isLoading, user])

  useEffect(() => {
    setAccessToken(token)
  }, [token])

  useEffect(() => {
    setAccessTokenFactory(({ allowInteractive = true, ...options } = {}) =>
      from(getAccessTokenSilently(options)).pipe(
        catchError((err: unknown) => {
          // Only consent the shopper has to grant again is recoverable here, and only by asking. A
          // caller that cannot host that prompt says so, and gets the error instead of a popup.
          if (allowInteractive && isConsentRequired(err)) {
            return from(getAccessTokenWithPopup(options))
          }
          return throwError(() => err)
        }),
        switchMap((accessToken) => {
          if (options?.updateTokenContext) {
            // reset the stores before updating the token context
            // to avoid error with in-flight request once the token is updated
            return resetTokenStore$(accessToken).pipe(
              map(() => {
                setAccessTokenState({ token: accessToken, lastError: undefined })
                return accessToken
              }),
            )
          }
          setAccessTokenState((current) =>
            current.lastError === undefined ? current : { ...current, lastError: undefined },
          )
          return of(accessToken)
        }),
        catchError((error: unknown) => {
          setAccessTokenState((current) => ({
            ...current,
            lastError: authErrorContext(error),
          }))
          return throwError(() => error)
        }),
      ),
    )
  }, [getAccessTokenSilently, getAccessTokenWithPopup])

  useEffect(() => {
    setLocalLogout(() => logout({ localOnly: true }))
  }, [logout])

  return <TokenContext.Provider value={token}>{children}</TokenContext.Provider>
}

TokenContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default TokenContextProvider
